<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { Search, LogOut, Loader2 } from 'lucide-vue-next'
import { PRIMARY_NAV_ITEMS, usePermissions, usePortalBrandingStore } from '@/shared'
import { useAuthStore } from '@/modules/auth'

/**
 * Emits definition following SKILL.md guidelines
 */
interface Emits {
  (e: 'select', id: string): void
  (e: 'search'): void
  (e: 'logout'): void
}

/**
 * Props definition
 */
interface Props {
  forcedCollapsed?: boolean
  activeModuleId?: string
}

const props = withDefaults(defineProps<Props>(), {
  activeModuleId: 'dashboards'
})

// Define emits using TypeScript interfaces
const emit = defineEmits<Emits>()

const { isVisible } = usePermissions()
const branding = usePortalBrandingStore()
const authStore = useAuthStore()
const router = useRouter()

const handleLogoClick = () => {
  router.push('/dashboard')
}

const handleLogout = () => {
  if (authStore.isLoggingOut) return
  emit('logout')
}

/**
 * UI State: manages rail expansion on hover
 */
const isHovered = ref(false)

/**
 * Handles module selection and updates active state
 * @param id Unique identifier of selected module
 */
const handleItemClick = (id: string) => {
  emit('select', id)
}

/**
 * Computed nav items that respect the activeModuleId prop
 */
const displayNavItems = computed(() => {
  return PRIMARY_NAV_ITEMS.filter((item) => isVisible(item)).map((item) => ({
    ...item,
    active: item.id === props.activeModuleId
  }))
})
</script>

<template>
  <aside
    class="relative z-50 flex h-screen min-h-0 flex-col bg-sidebar py-4 text-sidebar-foreground overflow-hidden shadow-xl"
    :class="[
      isHovered && !props.forcedCollapsed ? 'w-[72px] md:w-[200px]' : 'w-[72px] md:w-[80px]',
      props.forcedCollapsed ? 'transition-none' : 'transition-all duration-300 ease-in-out'
    ]"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <!-- Brand Logo Section -->
    <div class="mb-6 flex w-full shrink-0 items-center justify-center px-4">
      <div
        class="flex h-12 w-full items-center justify-center cursor-pointer transition-all duration-300"
        @click="handleLogoClick"
      >
        <!-- Full Logo (Desktop, expanded) -->
        <img
          v-show="isHovered && !props.forcedCollapsed"
          :src="branding.logoWhiteUrl"
          :alt="branding.appLabel"
          class="flex-shrink-0 object-contain transition-all duration-300 hidden md:block h-9 w-auto max-w-[160px] opacity-100"
        />
        <!-- Icon Logo (Desktop, collapsed) -->
        <img
          v-show="!isHovered || props.forcedCollapsed"
          :src="branding.logoIconWhiteUrl"
          :alt="branding.appLabel"
          class="flex-shrink-0 object-contain transition-all duration-300 hidden md:block h-10 w-10 opacity-100"
        />
        <!-- Mobile Logo -->
        <img
          :src="branding.logoIconWhiteUrl"
          :alt="branding.appLabel"
          class="flex-shrink-0 object-contain transition-all duration-300 h-8 w-8 md:hidden block"
        />
      </div>
    </div>

    <!-- Search Tool Trigger -->
    <div class="mb-4 shrink-0 px-4">
      <div
        class="group flex h-12 w-full cursor-pointer items-center rounded-xl transition-all duration-200 hover:bg-sidebar-accent/15 active:scale-95"
        :class="
          isHovered && !props.forcedCollapsed
            ? 'justify-center md:justify-start md:px-2 md:gap-4'
            : 'justify-center'
        "
        @click="emit('search')"
        title="Search"
      >
        <Search
          class="h-6 w-6 flex-shrink-0 text-sidebar-foreground/60 group-hover:text-sidebar-foreground transition-colors duration-200"
        />
        <span
          v-if="isHovered && !props.forcedCollapsed"
          class="text-sm font-medium text-sidebar-foreground/60 group-hover:text-sidebar-foreground transition-opacity duration-300 hidden md:block"
        >
          Search Nav
        </span>
      </div>
    </div>

    <!-- Navigation Menu -->
    <div class="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden app-scrollbar">
      <nav class="flex w-full flex-col space-y-2">
        <div
          v-for="item in displayNavItems"
          :key="item.id"
          class="group relative flex w-full cursor-pointer items-center px-4"
          @click="handleItemClick(item.id)"
        >
          <!-- Active indicator dot/bar -->
          <div
            v-if="item.active"
            class="absolute left-0 h-10 w-1 rounded-r-full bg-primary transition-all"
          />

          <!-- Nav Item Button -->
          <div
            :class="[
              'flex h-12 w-full items-center rounded-xl transition-all duration-200',
              isHovered && !props.forcedCollapsed
                ? 'justify-center md:justify-start md:px-2 md:gap-4'
                : 'justify-center',
              item.active
                ? 'bg-sidebar-accent text-sidebar-accent-foreground shadow-lg'
                : 'text-sidebar-foreground/60 hover:bg-sidebar-accent/15 hover:text-sidebar-foreground'
            ]"
          >
            <component :is="item.icon" class="h-6 w-6 flex-shrink-0" />
            <span
              v-if="isHovered && !props.forcedCollapsed"
              class="whitespace-nowrap text-sm font-semibold transition-opacity duration-300 hidden md:block"
            >
              {{ item.label }}
            </span>
          </div>

          <!-- Collapsed Label Preview (Tooltip Style) -->
          <span
            v-if="!isHovered || props.forcedCollapsed"
            class="absolute left-full ml-2 hidden scale-95 rounded bg-elevated border border-border px-2 py-1 text-xs font-medium text-elevated-foreground opacity-0 transition-all group-hover:block group-hover:scale-100 group-hover:opacity-100"
          >
            {{ item.label }}
          </span>
        </div>
      </nav>
    </div>

    <!-- Footer Area (Logout) -->
    <div class="mt-auto shrink-0 px-4 pb-2 pt-2 border-t border-sidebar-border/20">
      <button
        type="button"
        :disabled="authStore.isLoggingOut"
        class="group relative flex h-12 w-full cursor-pointer items-center rounded-xl transition-all duration-200 hover:bg-destructive/15 text-sidebar-foreground/60 hover:text-destructive active:scale-95 disabled:pointer-events-none disabled:opacity-50"
        :class="
          isHovered && !props.forcedCollapsed
            ? 'justify-center md:justify-start md:px-2 md:gap-4'
            : 'justify-center'
        "
        @click="handleLogout"
        title="Log out"
      >
        <Loader2
          v-if="authStore.isLoggingOut"
          class="h-6 w-6 flex-shrink-0 animate-spin transition-colors duration-200"
        />
        <LogOut v-else class="h-6 w-6 flex-shrink-0 transition-colors duration-200" />
        <span
          v-if="isHovered && !props.forcedCollapsed"
          class="whitespace-nowrap text-sm font-semibold transition-opacity duration-300 hidden md:block"
        >
          {{ authStore.isLoggingOut ? 'Logging out...' : 'Log out' }}
        </span>

        <!-- Collapsed Label Preview (Tooltip Style) -->
        <span
          v-if="!isHovered || props.forcedCollapsed"
          class="absolute left-full ml-2 hidden scale-95 rounded bg-elevated border border-border px-2 py-1 text-xs font-medium text-destructive opacity-0 transition-all group-hover:block group-hover:scale-100 group-hover:opacity-100 whitespace-nowrap z-50 shadow-md"
        >
          {{ authStore.isLoggingOut ? 'Logging out...' : 'Log out' }}
        </span>
      </button>
    </div>
  </aside>
</template>
